// src/App.tsx (기존 코드에 추가)
import {FC} from 'react';
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom';
import {createTheme, CssBaseline, GlobalStyles, ThemeProvider} from '@mui/material';
import {QueryProvider} from './providers/QueryProvider';
import {AuthProvider} from './contexts/AuthContext';
import {ProtectedRoute} from './components/auth/ProtectedRoute';
import {DashboardLayout} from './components/layout/DashboardLayout';
import {Login} from './components/auth/Login';

// Library 컴포넌트들
import {LibraryList} from './components/library/LibraryList';
import {LibraryShow} from './components/library/LibraryShow';
import {LibraryForm} from "./components/library/LibraryForm.tsx";
import {LibraryStats} from './components/library/LibraryStats';
import {LibrarySectionList} from './components/library/LibrarySectionList';

// Wordbook 컴포넌트들
import {WordbookList} from './components/wordbook/WordbookList';
import {WordbookShow} from './components/wordbook/WordbookShow';
import {WordbookStats} from './components/wordbook/WordbookStats';

import {WordbookForm} from "./components/wordbook/WordbookForm.tsx";
// WordbookGroup 컴포넌트들 (새로 추가)
import {WordbookGroupList} from './components/wordbookGroup/WordbookGroupList.tsx';
import {WordbookGroupShow} from './components/wordbookGroup/WordbookGroupShow.tsx';
import {WordbookGroupStats} from './components/wordbookGroup/WordbookGroupStats.tsx';
import {WordbookGroupForm} from "./components/wordbookGroup/WordbookGroupForm.tsx";

// User 컴포넌트들
import UserList from './components/user/UserList.tsx';
import UserShow from './components/user/UserShow.tsx';

// Push 컴포넌트들
import PushList from './components/push/PushList.tsx';

// LocalPush 컴포넌트들
import LocalPushScheduleList from './components/localPush/LocalPushScheduleList.tsx';

import {DictionaryWordList} from './components/dictionary/DictionaryWordList.tsx';
import DictionaryReviewList from './components/dictionaryReview/DictionaryReviewList';
import {AdminBatchJobList} from './components/batch/AdminBatchJobList.tsx';
import {AiLogList} from './components/aiLog/AiLogList.tsx';
import AiValidationSummary from './components/aiValidation/AiValidationSummary';
import {WordReportList} from './components/report/WordReportList.tsx';
import {IapProductsPage} from './components/iap/IapProductsPage.tsx';
import {NoticeList} from './components/notice/NoticeList';
import {AppVersionList} from './components/appVersion/AppVersionList';
import {InquiryList} from './components/inquiry/InquiryList';
import {SystemSettings} from './components/settings/SystemSettings';
import {DashboardHome} from './components/dashboard/DashboardHome';
import ExceptionGroupList from './components/exception/ExceptionGroupList';
import ExceptionGroupDetail from './components/exception/ExceptionGroupDetail';
import SubscriptionLimitsPage from './components/subscription/SubscriptionLimitsPage';
import TtsManagementPage from './pages/TtsManagementPage.tsx';
import AdminTtsVoiceConfigPage from './pages/AdminTtsVoiceConfigPage.tsx';
import UserActivityAnalyticsPage from './pages/UserActivityAnalyticsPage';
import RevenueAnalyticsPage from './pages/RevenueAnalyticsPage';
import LearningAnalyticsPage from './pages/LearningAnalyticsPage';
import AiOperationsAnalyticsPage from './pages/AiOperationsAnalyticsPage';
import AiImagesPage from './pages/AiImagesPage';
import {EppiCostumeList} from './components/eppiCostume/EppiCostumeList';
import {EppiCostumeFormPage} from './components/eppiCostume/EppiCostumeFormPage';
import {EppiCategoryList} from './components/eppiCostume/EppiCategoryList';
import {MissionList} from './components/mission/MissionList';

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#3b82f6',
            light: '#60a5fa',
            dark: '#2563eb',
        },
        secondary: {
            main: '#6366f1',
        },
        success: {
            main: '#22c55e',
        },
        warning: {
            main: '#f59e0b',
        },
        error: {
            main: '#ef4444',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
        text: {
            primary: '#1e293b',
            secondary: '#64748b',
        },
        divider: '#e2e8f0',
    },
    typography: {
        fontFamily: [
            '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"',
            'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif',
        ].join(','),
        h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
        h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },
        h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
        body1: { fontSize: '0.875rem', lineHeight: 1.5 },
        body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
        caption: { fontSize: '0.75rem', lineHeight: 1.4 },
        button: { fontSize: '0.8125rem', fontWeight: 500 },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { textTransform: 'none', borderRadius: 8, fontWeight: 500 },
                sizeSmall: { height: 32, fontSize: '0.75rem' },
                sizeMedium: { height: 36 },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid #e2e8f0',
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-head': {
                        backgroundColor: '#f8fafc',
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        color: '#475569',
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: { fontSize: '0.8125rem', padding: '12px 16px' },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: { '&:last-child td': { borderBottom: 0 } },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { fontWeight: 500, fontSize: '0.75rem' },
                sizeSmall: { height: 24 },
            },
        },
        MuiTextField: {
            defaultProps: { size: 'small' },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: { borderRadius: 8 },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: { borderRadius: 12 },
            },
        },
    },
});

const globalStyles = (
    <GlobalStyles
        styles={{
            '#root': {
                width: '100%',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            },
            body: {
                margin: 0,
                padding: 0,
                display: 'block !important',
            },
        }}
    />
);

const App: FC = () => {
    return (
        <QueryProvider>
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                {globalStyles}
                <AuthProvider>
                    <BrowserRouter basename={import.meta.env.BASE_URL}>
                        <Routes>
                            {/* 로그인 페이지 */}
                            <Route path="/login" element={<Login/>}/>

                            {/* 보호된 라우트 */}
                            <Route
                                path="/*"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <Routes>
                                                {/* Library 라우트 */}
                                                <Route path="/library" element={<LibraryList/>}/>
                                                <Route path="/library/:id" element={<LibraryShow/>}/>
                                                <Route path="/library/:id/edit" element={<LibraryForm/>}/>
                                                <Route path="/library/create" element={<LibraryForm/>}/>
                                                <Route path="/library/stats" element={<LibraryStats/>}/>
                                                <Route path="/library/sections" element={<LibrarySectionList/>}/>

                                                {/* Wordbook 라우트 */}
                                                <Route path="/wordbook" element={<WordbookList/>}/>
                                                <Route path="/wordbook/:id" element={<WordbookShow/>}/>
                                                <Route path="/wordbook/:id/edit" element={<WordbookForm/>}/>
                                                <Route path="/wordbook/create" element={<WordbookForm/>}/>
                                                <Route path="/wordbook/create/:groupId" element={<WordbookForm/>}/>
                                                <Route path="/wordbook/stats" element={<WordbookStats/>}/>

                                                {/* WordbookGroup 라우트 (새로 추가) */}
                                                <Route path="/wordbook-group" element={<WordbookGroupList/>}/>
                                                <Route path="/wordbook-group/:id" element={<WordbookGroupShow/>}/>
                                                <Route path="/wordbook-group/:id/edit" element={<WordbookGroupForm/>}/>
                                                <Route path="/wordbook-group/create" element={<WordbookGroupForm/>}/>
                                                <Route path="/wordbook-group/stats" element={<WordbookGroupStats/>}/>

                                                {/* User 라우트 */}
                                                <Route path="/user" element={<UserList/>}/>
                                                <Route path="/user/:id" element={<UserShow/>}/>

                                                {/* Push 라우트 */}
                                                <Route path="/push" element={<PushList/>}/>

                                                <Route path="/dictionary" element={<DictionaryWordList/>}/>
                                                <Route path="/dictionary/review" element={<DictionaryReviewList/>}/>
                                                <Route path="/batch/jobs" element={<AdminBatchJobList/>}/>
                                                <Route path="/ai/request-logs" element={<AiLogList/>}/>
                                                <Route path="/ai/validation" element={<AiValidationSummary/>}/>
                                                <Route path="/reports" element={<WordReportList/>}/>

                                                {/* IAP 라우트 */}
                                                <Route path="/iap-products" element={<IapProductsPage/>}/>

                                                {/* Notice 라우트 */}
                                                <Route path="/notices" element={<NoticeList/>}/>

                                                {/* AppVersion 라우트 */}
                                                <Route path="/app-versions" element={<AppVersionList/>}/>

                                                {/* Inquiry 라우트 */}
                                                <Route path="/inquiries" element={<InquiryList/>}/>

                                                {/* LocalPush 라우트 */}
                                                <Route path="/local-push" element={<LocalPushScheduleList/>}/>

                                                {/* TTS 관리 라우트 */}
                                                <Route path="/tts-management" element={<TtsManagementPage/>}/>
                                                <Route path="/tts-voice-config" element={<AdminTtsVoiceConfigPage/>}/>

                                                {/* Subscription 라우트 */}
                                                <Route path="/subscription-limits" element={<SubscriptionLimitsPage/>}/>

                                                {/* SystemSettings 라우트 */}
                                                <Route path="/system-settings" element={<SystemSettings/>}/>

                                                {/* Exception 라우트 */}
                                                <Route path="/exceptions" element={<ExceptionGroupList/>}/>
                                                <Route path="/exceptions/:groupId" element={<ExceptionGroupDetail/>}/>

                                                {/* Analytics 라우트 */}
                                                <Route path="/analytics/user-activity" element={<UserActivityAnalyticsPage />} />
                                                <Route path="/analytics/revenue" element={<RevenueAnalyticsPage />} />
                                                <Route path="/analytics/learning" element={<LearningAnalyticsPage />} />
                                                <Route path="/analytics/ai-operations" element={<AiOperationsAnalyticsPage />} />

                                                {/* AI 이미지 생성 */}
                                                <Route path="/ai-images" element={<AiImagesPage/>}/>
                                                <Route path="/ai-images/groups/:groupId" element={<AiImagesPage/>}/>

                                                {/* 에삐 스티커북 (내부 모델명은 eppi_costumes 유지) */}
                                                <Route path="/eppi-costumes" element={<EppiCostumeList/>}/>
                                                <Route path="/eppi-costumes/new" element={<EppiCostumeFormPage/>}/>
                                                <Route path="/eppi-costumes/:code/edit" element={<EppiCostumeFormPage/>}/>
                                                <Route path="/eppi-categories" element={<EppiCategoryList/>}/>

                                                {/* 미션 관리 */}
                                                <Route path="/missions" element={<MissionList/>}/>

                                                {/* Dashboard 라우트 */}
                                                <Route path="/dashboard" element={<DashboardHome/>}/>

                                                {/* 기본 리다이렉트 */}
                                                <Route path="/" element={<Navigate to="/dashboard" replace/>}/>
                                            </Routes>
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                        </Routes>
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </QueryProvider>
    );
};

export default App;
